import { create } from 'zustand';
import { authApi } from '../api/auth';
import useOrganizationStore from './organizationStore';
import type { AuthStore } from './types';

const useAuthStore = create<AuthStore>((set, get) => ({
  // Initial state
  user: null,
  isAuthenticated: false,
  isLoading: true,
  accessToken: localStorage.getItem('accessToken'),
  refreshToken: localStorage.getItem('refreshToken'),

  // Actions
  setUser: (user) => {
    set({ user, isAuthenticated: !!user });
  },

  setTokens: (accessToken, refreshToken) => {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    set({ accessToken, refreshToken });
  },

  clearAuth: () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    // Also clear organization context on logout
    useOrganizationStore.getState().reset();
    set({
      user: null,
      isAuthenticated: false,
      accessToken: null,
      refreshToken: null,
    });
  },

  setLoading: (isLoading) => {
    set({ isLoading });
  },

  // Initialize auth state from stored tokens
  initialize: async () => {
    const { accessToken } = get();

    if (!accessToken) {
      set({ isLoading: false });
      return;
    }

    try {
      const response = await authApi.getMe();
      set({
        user: response.data,
        isAuthenticated: true,
        isLoading: false,
      });

      // Load user's organizations after successful auth
      try {
        await useOrganizationStore.getState().loadUserOrganizations();
      } catch (orgError) {
        console.error('Failed to load organizations:', orgError);
        // Don't fail auth if organizations fail to load
        // User will be prompted to create one
      }
    } catch (error) {
      // Token is invalid, clear it
      get().clearAuth();
      set({ isLoading: false });
    }
  },
}));

export { useAuthStore };
export default useAuthStore;
